import React from "react";
import { motion } from "framer-motion";
import { GoogleLogo, FacebookLogo, TrendUp, TrendDown } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { formatCurrency, formatRoas, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_VARIANT = { live: "success", paused: "warning", closed: "secondary" };
const PLATFORM_ICON = { google: GoogleLogo, meta: FacebookLogo };

export default function CampaignCard({ campaign, onClick }) {
  const PlatformIcon = PLATFORM_ICON[campaign.platform] || GoogleLogo;
  const budgetUsedPct = campaign.budget ? Math.min(100, (campaign.spend / campaign.budget) * 100) : 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      whileHover={{ y: -3 }}
      onClick={() => onClick(campaign)}
      className="cursor-pointer"
      data-testid={`campaign-card-${campaign.id}`}
    >
      <Card className="p-4 h-full hover:border-primary/40 transition-colors duration-200 relative overflow-hidden">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center shrink-0">
              <PlatformIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid={`campaign-name-${campaign.id}`}>{campaign.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{campaign.platform} Ads</p>
            </div>
          </div>
          <Badge variant={STATUS_VARIANT[campaign.status]} className="capitalize shrink-0" data-testid={`campaign-status-${campaign.id}`}>
            {campaign.status}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Spend</p>
            <p className="text-lg font-semibold tabular-nums" data-testid={`campaign-spend-${campaign.id}`}>{formatCurrency(campaign.spend)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">ROAS</p>
            <p className={cn("text-lg font-semibold tabular-nums flex items-center gap-1", campaign.roas >= 2 ? "text-success" : "text-warning")}>
              {campaign.roas >= 2 ? <TrendUp className="h-3.5 w-3.5" weight="bold" /> : <TrendDown className="h-3.5 w-3.5" weight="bold" />}
              {formatRoas(campaign.roas)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs border-t border-border pt-3">
          <div>
            <p className="text-muted-foreground">CTR</p>
            <p className="font-medium tabular-nums">{formatPercent(campaign.ctr)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">CPC</p>
            <p className="font-medium tabular-nums">{formatCurrency(campaign.cpc)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">CPL</p>
            <p className="font-medium tabular-nums">{formatCurrency(campaign.cpl)}</p>
          </div>
        </div>

        <div className="mt-3">
          <div className="h-1 w-full bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${budgetUsedPct}%` }} />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{budgetUsedPct.toFixed(0)}% of {formatCurrency(campaign.budget)} budget used</p>
        </div>
      </Card>
    </motion.div>
  );
}
